import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import VendorlyLogo from "@/components/VendorlyLogo";
import { ArrowRight } from "@phosphor-icons/react";
import { toast, Toaster } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      toast.success("Welcome back!");
      nav("/app");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Login failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-brand-bg text-brand-text flex flex-col dot-bg" data-testid="login-page">
      <Toaster position="top-right" richColors />
      <div className="max-w-7xl w-full mx-auto px-6 lg:px-10 py-6">
        <Link to="/" className="inline-flex items-center gap-2" data-testid="login-brand">
          <VendorlyLogo size={30} />
          <span className="font-heading font-bold text-xl tracking-tight">Vendorly</span>
        </Link>
      </div>
      <div className="flex-1 flex items-center justify-center px-6 py-10">
        <div className="w-full max-w-md bg-white border border-brand-border rounded-3xl p-8 sm:p-10 shadow-[0_10px_40px_rgba(44,42,41,0.06)]">
          <div className="text-xs uppercase tracking-[0.2em] text-brand-secondary mb-3">Vendor sign in</div>
          <h1 className="font-heading font-bold text-3xl tracking-tight">Welcome back.</h1>
          <p className="text-brand-text/70 mt-2">Enter your details to open your dashboard.</p>
          <form onSubmit={submit} className="mt-8 space-y-4">
            <div>
              <label className="text-sm font-semibold">Email</label>
              <input type="email" required value={email} onChange={(e)=>setEmail(e.target.value)}
                className="mt-1.5 w-full h-12 px-4 rounded-xl bg-brand-bg border border-brand-border focus:border-brand-primary focus:ring-1 focus:ring-brand-primary outline-none transition-colors"
                data-testid="login-email" placeholder="you@shop.com" />
            </div>
            <div>
              <label className="text-sm font-semibold">Password</label>
              <input type="password" required value={password} onChange={(e)=>setPassword(e.target.value)}
                className="mt-1.5 w-full h-12 px-4 rounded-xl bg-brand-bg border border-brand-border focus:border-brand-primary focus:ring-1 focus:ring-brand-primary outline-none transition-colors"
                data-testid="login-password" placeholder="••••••••" />
              <div className="mt-2 text-right">
                <Link to="/forgot-password" className="text-xs font-semibold text-brand-primary hover:underline" data-testid="login-forgot">Forgot password?</Link>
              </div>
            </div>
            <button type="submit" disabled={busy}
              className="w-full inline-flex items-center justify-center gap-2 h-12 rounded-full bg-brand-primary hover:bg-brand-primaryHover text-white font-semibold transition-colors disabled:opacity-60"
              data-testid="login-submit">
              {busy ? "Signing in…" : (<>Sign in <ArrowRight size={18} weight="bold" /></>)}
            </button>
          </form>
          <div className="mt-6 text-sm text-brand-text/70">
            New to Vendorly? <Link to="/register" className="text-brand-primary font-semibold hover:underline" data-testid="login-to-register">Create an account</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
